import type { ButtonHTMLAttributes, ComponentPropsWithRef, ReactNode } from 'react';
import { DISCLAIMERS } from '@erectus/shared';

/** Minimal UI kit. Phase 5 makes it pretty; until then: legible and accessible. */

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

const BUTTON_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-ink text-white hover:bg-ink-soft',
  secondary: 'bg-white text-ink border border-line hover:bg-surface',
  ghost: 'bg-transparent text-ink-soft hover:bg-surface',
  danger: 'bg-white text-[color:var(--color-state-slouch)] border border-[color:var(--color-state-slouch)] hover:bg-[color:var(--color-state-slouch-soft)]',
};

export function Button({
  variant = 'primary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: ButtonVariant }): ReactNode {
  return (
    <button
      {...props}
      className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-50 ${BUTTON_STYLES[variant]} ${className}`}
    />
  );
}

/** Takes a ref so callers can scroll a specific card into view (React 19
 *  passes ref as an ordinary prop to function components). */
export function Card({
  className = '',
  ...props
}: ComponentPropsWithRef<'div'>): ReactNode {
  return (
    <div
      {...props}
      className={`rounded-2xl border border-line bg-card p-4 shadow-[0_1px_2px_rgba(15,23,42,0.04)] ${className}`}
    />
  );
}

export function SectionTitle({ children }: { children: ReactNode }): ReactNode {
  return <h2 className="mb-2 text-sm font-semibold tracking-wide text-muted uppercase">{children}</h2>;
}

export function Spinner({ label = 'Loading' }: { label?: string }): ReactNode {
  return (
    <div role="status" aria-live="polite" className="flex items-center gap-2 text-sm text-muted">
      <span
        aria-hidden
        className="size-4 animate-spin rounded-full border-2 border-line border-t-ink"
      />
      {label}
    </div>
  );
}

export function ErrorState({
  title,
  message,
  action,
}: {
  title: string;
  message: string;
  action?: ReactNode;
}): ReactNode {
  return (
    <Card className="border-[color:var(--color-state-slouch)] bg-[color:var(--color-state-slouch-soft)]">
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="mt-1 text-sm text-ink-soft">{message}</p>
      {action ? <div className="mt-3">{action}</div> : null}
    </Card>
  );
}

export function EmptyState({ title, message, action }: { title: string; message: string; action?: ReactNode }): ReactNode {
  return (
    <Card className="text-center">
      <h2 className="text-base font-semibold">{title}</h2>
      <p className="mt-1 text-sm text-muted">{message}</p>
      {action ? <div className="mt-4 flex justify-center">{action}</div> : null}
    </Card>
  );
}

/**
 * Fixed medical disclaimer. Required at the bottom of every report and exercise
 * screen (docs/LEGAL_PRIVACY.md §2 item 4) — the wording comes from
 * @erectus/shared so it cannot drift.
 */
export function Disclaimer({ className = '' }: { className?: string }): ReactNode {
  return (
    <p className={`text-xs leading-relaxed text-muted ${className}`}>{DISCLAIMERS.short}</p>
  );
}

export function AccuracyNote(): ReactNode {
  return <p className="text-xs text-muted">{DISCLAIMERS.accuracy}</p>;
}

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}): ReactNode {
  return (
    <label className="block">
      <span className="text-sm font-medium text-ink">{label}</span>
      {hint ? <span className="mt-0.5 block text-xs text-muted">{hint}</span> : null}
      <span className="mt-1.5 block">{children}</span>
    </label>
  );
}

export function TextInput({
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>): ReactNode {
  return (
    <input
      {...props}
      className={`w-full rounded-xl border border-line bg-white px-3 py-2.5 text-sm outline-none focus:border-ink ${className}`}
    />
  );
}

export function Toggle({
  checked,
  onChange,
  label,
  description,
}: {
  checked: boolean;
  onChange(value: boolean): void;
  label: string;
  description?: string;
}): ReactNode {
  return (
    <label className="flex cursor-pointer items-start gap-3">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 size-5 shrink-0 accent-[color:var(--color-ink)]"
      />
      <span>
        <span className="block text-sm font-medium">{label}</span>
        {description ? <span className="block text-xs text-muted">{description}</span> : null}
      </span>
    </label>
  );
}

export function Pill({
  active,
  children,
  className = 'px-3',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { active?: boolean }): ReactNode {
  return (
    <button
      {...props}
      aria-pressed={active}
      className={`whitespace-nowrap rounded-full border py-1.5 text-sm font-medium transition ${className} ${
        active ? 'border-ink bg-ink text-white' : 'border-line bg-white text-ink-soft'
      }`}
    >
      {children}
    </button>
  );
}
